import { DateTime } from 'luxon';

export const dateDisplayOptions: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

export const timeDisplayOptions: Intl.DateTimeFormatOptions = {
  timeZoneName: 'shortGeneric',
  hour: 'numeric',
  minute: 'numeric',
};

/**
 * These formatters accept an absent date and return the empty string, so a shift saved without a
 * start date renders blank instead of throwing and blanking the whole page. Callers that want a
 * visible placeholder pick their own, e.g. `asDateString(d, tz, lang) || '-'`. An invalid `Date` is
 * deliberately NOT absorbed: it still renders as "Invalid Date", because callers that fall back on
 * an empty result would otherwise present broken data as a legitimate value.
 */
export function asTimeString(
  date: Date | undefined,
  selectedTimezone?: string,
  locales?: Intl.LocalesArgument,
  options?: { removeTimeZone: boolean }
): string {
  if (!date) return '';

  const extraOptions = options?.removeTimeZone
    ? {
        timeZone: undefined,
      }
    : {};

  return date.toLocaleString(locales, {
    ...timeDisplayOptions,
    ...extraOptions,
    timeZone: selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

export function asDateString(
  date: Date | undefined,
  selectedTimezone?: string,
  locales?: Intl.LocalesArgument
): string {
  if (!date) return '';

  return date.toLocaleDateString(locales, { ...dateDisplayOptions, timeZone: selectedTimezone });
}

export function asDateTimeString(
  date: Date | undefined,
  selectedTimezone?: string,
  locales?: Intl.LocalesArgument
): string {
  if (!date) return '';

  return date.toLocaleTimeString(locales, { ...dateDisplayOptions, timeZone: selectedTimezone });
}

// Short date format for table columns (e.g., "Jan 21, 2026")
const shortDateOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

export function formatShiftDate(
  startDateTime?: Date | string,
  selectedTimezone?: string,
  locales?: Intl.LocalesArgument
): string {
  if (!startDateTime) return '';
  const date = typeof startDateTime === 'string' ? new Date(startDateTime) : startDateTime;
  return date.toLocaleDateString(locales, { ...shortDateOptions, timeZone: selectedTimezone });
}

export function formatShiftTime(
  startDateTime?: Date | string,
  selectedTimezone?: string,
  locales?: Intl.LocalesArgument
): string {
  if (!startDateTime) return '';
  const date = typeof startDateTime === 'string' ? new Date(startDateTime) : startDateTime;
  return asTimeString(date, selectedTimezone, locales, { removeTimeZone: true });
}

/**
 * The short timezone abbreviation for an instant in a zone, e.g. "PST" / "EST" / "GMT+5:30". This is
 * the location-specific short name (Intl `timeZoneName: 'short'`), distinct from the `'shortGeneric'`
 * label formatShiftTime already inlines ("PT"/"ET") — the mismatch popover shows the specific one.
 * Returns '' for an absent/invalid date or an unknown IANA zone (Intl throws RangeError on the latter).
 *
 * `locales` is typed `string | string[]` rather than `Intl.LocalesArgument`: the `Intl.DateTimeFormat`
 * constructor's lib overload only accepts `string | string[]`, so the wider `Intl.LocalesArgument`
 * (which includes `Intl.Locale`) does not type-check here. Every caller passes the app's resolved
 * BCP-47 locale string (see useLocale), so the narrower type is also the accurate one.
 */
export function getTimeZoneAbbreviation(
  date: Date | undefined,
  timeZone: string,
  locales?: string | string[]
): string {
  if (!date || isNaN(date.getTime()) || !timeZone) return '';
  try {
    const timeZoneName = new Intl.DateTimeFormat(locales, { timeZone, timeZoneName: 'short' })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value;
    return timeZoneName ?? '';
  } catch {
    return '';
  }
}

/**
 * Whether a string resolves to a valid IANA time zone. A guard for callers that hand the zone to
 * Intl / toLocaleString, which throw RangeError on an unknown zone. Uses luxon's zone resolution,
 * the same validity signal getZoneOffsetMinutes relies on.
 */
export function isValidTimeZone(timeZone: string | undefined): boolean {
  if (!timeZone) return false;
  return DateTime.local().setZone(timeZone).isValid;
}

/**
 * The UTC offset (in minutes) a zone is at for a given instant, or undefined if the date/zone is
 * invalid. DST-aware because it resolves the offset AT that instant, so the same zone can report a
 * different value in summer vs winter.
 */
export function getZoneOffsetMinutes(date: Date | undefined, timeZone: string): number | undefined {
  if (!date || isNaN(date.getTime()) || !timeZone) return undefined;
  const zoned = DateTime.fromJSDate(date, { zone: timeZone });
  return zoned.isValid ? zoned.offset : undefined;
}

/**
 * Whether two zones are at different UTC offsets AT a given instant. Compares the numeric offsets, not
 * the IANA strings, so two zones that happen to share an offset at that instant (e.g. Europe/London
 * and UTC in winter) are treated as the same and do NOT count as a mismatch. Returns false when either
 * offset cannot be resolved (no reliable basis to claim a difference).
 */
export function doZonesDifferAtInstant(
  date: Date | undefined,
  zoneA: string,
  zoneB: string
): boolean {
  const offsetA = getZoneOffsetMinutes(date, zoneA);
  const offsetB = getZoneOffsetMinutes(date, zoneB);
  if (offsetA === undefined || offsetB === undefined) return false;
  return offsetA !== offsetB;
}

/**
 * Bare "H:MM AM/PM" with no timezone label (unlike asTimeString), or '' for an absent/invalid date.
 * Returns '' for an unknown/malformed zone too: toLocaleTimeString throws RangeError on such a zone,
 * so this mirrors getTimeZoneAbbreviation's try/catch and degrades gracefully instead of crashing.
 */
function formatBareTime(
  date: Date | undefined,
  timeZone: string,
  locales?: string | string[]
): string {
  if (!date || isNaN(date.getTime())) return '';
  try {
    return date.toLocaleTimeString(locales, { hour: 'numeric', minute: 'numeric', timeZone });
  } catch {
    return '';
  }
}

/**
 * A start–end range rendered in one zone with a single trailing short abbreviation, e.g.
 * "10:00 AM - 12:00 PM PST" (the mismatch popover's per-zone value). An open-ended shift renders just
 * its start ("10:00 AM PST"); an empty range renders ''.
 */
export function formatShiftTimeRangeWithZone(
  start: Date | undefined,
  end: Date | undefined,
  timeZone: string,
  locales?: string | string[]
): string {
  const startText = formatBareTime(start, timeZone, locales);
  const endText = formatBareTime(end, timeZone, locales);
  const abbreviation = getTimeZoneAbbreviation(start ?? end, timeZone, locales);
  const suffix = abbreviation ? ` ${abbreviation}` : '';
  if (startText && endText) return `${startText} - ${endText}${suffix}`;
  if (startText) return `${startText}${suffix}`;
  return '';
}

/** Extracts the YYYY-MM-DD portion from a Date, or returns '' if invalid/undefined. */
export function toISODateString(d: Date | undefined, timezone: string): string {
  if (!d || isNaN(d.getTime())) return '';
  const timezoneDate = DateTime.fromJSDate(d, { zone: timezone });
  if (!timezoneDate.isValid) return '';
  const year = timezoneDate.year;
  const month = String(timezoneDate.month).padStart(2, '0');
  const day = String(timezoneDate.day).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Extracts timezone HH:mm from a Date, or empty string if invalid/undefined. */
export function toTimezoneTimeString(d: Date | undefined, timezone: string): string {
  if (!d || isNaN(d.getTime())) return '';
  const timezoneDate = DateTime.fromJSDate(d, { zone: timezone });
  if (!timezoneDate.isValid) return '';
  const h = String(timezoneDate.hour).padStart(2, '0');
  const m = String(timezoneDate.minute).padStart(2, '0');
  if (!h || !m) {
    return '';
  }
  return `${h}:${m}`;
}

/** Checks whether a string is a complete HH:MM time value. */
export function isCompleteTime(v: string): boolean {
  return /^\d{2}:\d{2}$/.test(v);
}

/** Combines a YYYY-MM-DD date string and HH:mm time string into a Date with the given timezone, or undefined if invalid. */
export function buildDateTimeFromParts(
  dateStr: string,
  timeStr: string,
  timezone: string
): Date | undefined {
  if (!dateStr || !timeStr) return undefined;
  const d = DateTime.fromISO(`${dateStr}T${timeStr}`, { zone: timezone });
  if (!d.isValid) return undefined;
  return d.toJSDate();
}
